// HOME screen markup — IDs/classes preserved for the CSS and for every render
// seam (pathHome/resumeGame/homeGate/journey/characters/homeProgress).
// 2026-09-06 redesign (Eric's light-theme mockup): compact brand row, storybook
// hero banner (media/home/hero-monastery.jpg), primary CTA high on the page,
// illustrated arcade tiles, rules collapsed into "How scoring works". Layout
// order for the classic (non-path) home is set with flex `order` in
// home-redesign.css so every render seam keeps its element and owner.
//
// PROGRESSION (PLAN-progression.md §5): the level badge + XP bar and the
// "Continue your Game" card are children of .home-card; ui/homeProgress.ts
// fills them and they stay hidden for guests.
import { initHomeProgress } from '../ui/homeProgress';
import { modePillHTML } from '../ui/modeSwitcher';
import { icon } from '../ui/icons';
import { etDateString } from '../today';
import './home-redesign.css';

initHomeProgress();

/** The hero's language (Eric's 2026-09-06 mockup): "A little faith. A little
    fun." is the DEFAULT and changes with the liturgical day — Sundays greet
    the Lord's Day; holy days can slot in here later (feasts.ts has the
    calendar when we want it). Pure so tests can pin the branches. */
export function heroLines(todayIso: string = etDateString()): { t1: string; t2: string; sub: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(todayIso || '');
  const dow = m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay() : -1;
  if (dow === 0) return { t1: 'Happy Sunday.', t2: '', sub: 'A little faith. A little fun. Ready for today’s trivia?' };
  return { t1: 'A little faith.', t2: 'A little fun.', sub: 'Ready for today’s trivia?' };
}
const HERO = heroLines();

export const homeHTML = /* html */ `
<div id="screenHome" class="home hidden">
  <!-- THE HOME CARD — transparent wrapper (display:contents) in classic mode;
       path mode paints it as one card (flag currently off). -->
  <div class="home-card" id="homeCard">
    <div class="hero">
      <div class="hero-brand">
        <div class="crest" aria-hidden="true">✝</div>
        <div class="hero-lockup">
          <div class="title"><span class="app">Catholics.app</span></div>
          ${modePillHTML}
        </div>
        <!-- daily streak pill (renderStreakChip); path mode only -->
        <span class="path-streak hidden" id="pathStreak"></span>
      </div>
      <!-- Storybook hero banner (Eric's mockup): serif greeting on the calm
           left, soft-focus monastery behind, Brother Theo big in the
           foreground. NOTE: when the Higgsfield daily limit resets, generate a
           single composed "Theo standing in the courtyard" scene like the
           mockup's — until then the welcome PNG rides over the cloister art. -->
      <div class="home-heroart">
        <div class="hha-text">
          <div class="hha-title">${HERO.t1}</div>
          ${HERO.t2 ? `<div class="hha-title">${HERO.t2}</div>` : ''}
          <div class="hha-sub">${HERO.sub}</div>
        </div>
        <img class="hha-theo" src="/media/characters/theo-welcome.png" alt="" aria-hidden="true"/>
      </div>
    </div>

    <!-- CAREER LEVEL + XP BAR — painted by ui/homeProgress.ts; hidden for guests. -->
    <div class="home-prog hidden" id="homeProgress">
      <div class="hp-row">
        <span class="hp-level" id="hpLevel">Level 1</span>
        <span class="hp-rank hidden" id="hpRank"></span>
        <span class="hp-xp" id="hpXp"></span>
      </div>
      <div class="hp-bar" id="hpBar" role="progressbar" aria-valuemin="0" aria-valuenow="0" aria-valuemax="100">
        <span class="hp-bar-fill" id="hpBarFill"></span>
      </div>
    </div>

    <!-- CONTINUE YOUR GAME — the server-saved session ("Round 3 of 5 · $2,350
         banked"). Tapping it fires the primary play CTA (#startBtn). -->
    <button class="home-continue hidden" id="homeContinue" type="button">
      <span class="hcont-body">
        <span class="hcont-lbl">Continue your Game</span>
        <span class="hcont-line" id="homeContinueLine"></span>
      </span>
      <span class="hcont-go" aria-hidden="true">›</span>
    </button>

    <!-- DAILY STREAK card — painted by pathHome.ts via ui/streak.ts. -->
    <button class="home-streak hidden" id="homeStreak" type="button"></button>

    <!-- BROTHER THEO greeting (renderCompanionHome) — slim strip below the fold. -->
    <div id="theoCard" class="theo-card"></div>

    <!-- PRIMARY SURFACE — #startBtn is the one primary action (startOrResume);
         path chrome stays hidden while isPilgrimageEnabled() is false. -->
    <div class="path-main" id="pathMain">
      <div class="cta">
        <div class="path-home hidden" id="pathHome">
          <div class="path-meter-txt" id="pathMeterTxt"></div>
          <div class="path-meter">
            <div class="path-bar" id="pathBar" role="progressbar" aria-valuemin="0" aria-valuenow="0" aria-valuemax="10">
              <span class="path-bar-fill" id="pathBarFill"></span>
            </div>
          </div>
          <div class="path-station-line hidden" id="pathStationLine"></div>
        </div>
        <div class="cta-stack">
          <button class="btn" id="startBtn">Start Round</button>
          <div class="path-reward hidden" id="pathReward"></div>
        </div>
        <div class="cta-links">
          <button class="start-fresh hidden" id="startFreshLink" type="button">Start a new game</button>
          <button class="path-map-link hidden" id="pathMapLink" type="button">See the whole path →</button>
        </div>
        <!-- FRIENDLY PLAY — the calm thing under the loud thing. -->
        <button class="friendly-cta hidden" id="friendlyBtn" type="button">
          <span class="friendly-cta-t">${icon('book', 16)} Friendly Play</span>
          <span class="friendly-cta-s">Study at your own pace. No clock, no coins.</span>
        </button>
      </div>

      <!-- CONTENT HUB — tiles gated individually (applyHomeTiers). -->
      <div class="hub hidden" id="homeHub">
        <div class="hub-grid">
          <button class="hub-tile hidden" id="tilePilgrimage" type="button"><span class="hub-ic">${icon('compass', 18)}</span><span class="hub-name">Pilgrimage</span></button>
          <button class="hub-tile hidden" id="tileMonastery" type="button"><span class="hub-ic">${icon('church', 18)}</span><span class="hub-name">Monastery</span></button>
          <button class="hub-tile hidden" id="tileCollections" type="button"><span class="hub-ic">${icon('cards', 18)}</span><span class="hub-name">Collections</span></button>
          <button class="hub-tile" id="tileLeaderboard" type="button"><span class="hub-ic">${icon('trophy', 18)}</span><span class="hub-name">Leaderboard</span></button>
        </div>
      </div>
    </div>
  </div>

  <!-- TODAY'S PILGRIMAGE quest-log card (renderJourneyHome). -->
  <div id="pilgrimageCard" class="pilgrimage-card hidden"></div>

  <!-- ARCADE — unlocked after finishing a full game (progressive disclosure). -->
  <div class="arcade hidden" id="arcadeSection">
    <div class="arcade-head">
      <div class="arcade-title">Time for a play break.</div>
      <div class="arcade-note">${icon('sparkle', 14)} Games for a brighter day.</div>
    </div>
    <div class="arcade-locked" id="arcadeLocked">Finish a full game to unlock the Arcade.</div>
    <div class="arcade-grid hidden" id="arcadeGrid">
      <button class="arc-tile" data-game="match3"><span class="arc-art" style="background-image:url('/media/home/tile-faith-match.jpg')"></span><span class="arc-cap"><span class="arc-name">Faith Match</span><span class="arc-sub">Collect the saints.</span></span></button>
      <button class="arc-tile" data-game="maryMatch3"><span class="arc-art" style="background-image:url('/media/home/tile-mary-match3.jpg')"></span><span class="arc-cap"><span class="arc-name">Mother Mary Match-3</span><span class="arc-sub">Make beauty grow.</span></span></button>
      <button class="arc-tile" data-game="manna"><span class="arc-art" style="background-image:url('/media/home/tile-manna-drop.jpg')"></span><span class="arc-cap"><span class="arc-name">Manna Drop</span><span class="arc-sub">Catch the gifts.</span></span></button>
      <button class="arc-tile" data-game="wheel"><span class="arc-art" style="background-image:url('/media/home/tile-bonus-wheel.jpg')"></span><span class="arc-cap"><span class="arc-name">Bonus Wheel</span><span class="arc-sub">Spin, learn, win.</span></span></button>
    </div>
  </div>

  <!-- HOW SCORING WORKS — the rules wall, collapsed into one calm row. -->
  <details class="card rules rules-details">
    <summary class="rules-summary"><span class="rules-sum-ic">${icon('bulb', 18)}</span><span class="rules-sum-t">How scoring works</span><span class="rules-sum-chev" aria-hidden="true">›</span></summary>
    <div class="rules-row"><div class="ic">${icon('clock', 18)}</div><div class="tx">Every question starts at <b>$1,000</b>, holds for a breath, then drains <b>$100 a second</b>. Answer fast to bank more.</div></div>
    <div class="rules-row"><div class="ic">${icon('star', 18)}</div><div class="tx"><b>Answer right</b> to bank whatever's still on the clock.</div></div>
    <div class="rules-row"><div class="ic">${icon('dove', 18)}</div><div class="tx"><b>Wrong before the clock hits $0?</b> No penalty. After $0 (or time out), it's a flat <b>−$100</b> — a rough round can dip your bank below $0, and that's okay; you'll climb back.</div></div>
    <div class="rules-row"><div class="ic">${icon('gift', 18)}</div><div class="tx">Reach the halfway mark of each round for a <b>bonus break</b> — spins, games &amp; heavenly surprises.</div></div>
    <div class="rules-row"><div class="ic">${icon('coin', 18)}</div><div class="tx"><b>Dollars are your game bank.</b> Spend them on the monastery, the Poor Box, and treasures along the way.</div></div>
    <div class="rules-row"><div class="ic">${icon('trophy', 18)}</div><div class="tx"><b>XP measures your learning.</b> Every right answer earns XP — it builds your level, and the <b>Leaderboard ranks the faithful by lifetime XP</b>. The more you get right, the higher you climb.</div></div>
    <details class="rules-explain hidden" id="pilgExplainRow">
      <summary>${icon('compass', 15)} What is the Pilgrimage?</summary>
      <div class="rules-explain-body">A guided path of small steps — answer questions, visit a feast, restore the chapel — each one lighting up the next. Walk them all to gather the Twelve Apostles and reach the monastery. <button class="rules-explain-link" id="pilgExplainOpen" type="button">Open the map →</button></div>
    </details>
  </details>
</div>`;
